import React, { useState, useEffect, useRef } from 'react';
import { DEFAULT_STUDENT_MAJORS } from '../constants/majors';
import { 
  Typography, 
  Grid, 
  Box, 
  CircularProgress,
  Card,
  Paper,
  Button,
  Divider,
  Rating,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  InputAdornment,
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip as MuiTooltip
} from '@mui/material';
import {
  People as PeopleIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  FileDownload as DownloadIcon,
  Close as CloseIcon,
  AssignmentTurnedIn as PassIcon,
  Cancel as FailIcon,
  Print as PrintIcon,
  Group as GroupIcon,
  ChatBubbleOutlined as FeedbackCommentIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  MenuBook as CourseIcon,
  Quiz as QuizIcon,
  Assignment as WorkloadIcon
} from '@mui/icons-material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie
} from 'recharts';
import api from '../services/api';
import { GlassCard } from '../components/GlassCard';
import { OfficialEvaluationReportPrint } from '../components/OfficialEvaluationReportPrint';

const TypedBarChart = BarChart as any;
const TypedBar = Bar as any;
const TypedXAxis = XAxis as any;
const TypedYAxis = YAxis as any;
const TypedCartesianGrid = CartesianGrid as any;
const TypedTooltip = Tooltip as any;
const TypedPieChart = PieChart as any;
const TypedPie = Pie as any;
const TypedResponsiveContainer = ResponsiveContainer as any;

const CHART_COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#00bcd4', '#9e9e9e'];

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Paper 
        elevation={4}
        sx={{ 
          p: 2, 
          border: '1px solid rgba(0,0,0,0.08)', 
          maxWidth: { xs: 280, sm: 360, md: 420 }, 
          borderRadius: 3,
          bgcolor: 'background.paper',
          boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: 'primary.main', fontSize: '0.85rem' }}>
          สมรรถนะด้าน {data.name} (เฉลี่ยรวม: {data.คะแนนเฉลี่ย})
        </Typography>
        <Divider sx={{ my: 1 }} />
        {data.items && data.items.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, maxHeight: 220, overflowY: 'auto', pr: 0.5, mt: 1 }}>
            {data.items.map((item: any, idx: number) => (
              <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                <Typography 
                  variant="caption" 
                  title={item.label}
                  sx={{ 
                    flexGrow: 1, 
                    whiteSpace: 'normal', 
                    wordBreak: 'break-word', 
                    fontSize: '0.7rem', 
                    fontWeight: 500, 
                    color: 'text.secondary', 
                    lineHeight: 1.3,
                    cursor: 'help'
                  }}
                >
                  {item.label.length > 55 ? item.label.substring(0, 52) + '...' : item.label}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 'bold', minWidth: 40, textAlign: 'right', fontSize: '0.7rem', color: 'text.primary' }}>
                  {parseFloat(item.value).toFixed(2)}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="caption" color="textSecondary">ไม่มีรายละเอียดรายข้อ</Typography>
        )}
      </Paper>
    );
  }
  return null;
};

interface AdminDashboardProps {
  isSupervisorView?: boolean;
  supervisorMajor?: string;
}

const isMajorMatch = (studentMajor: string, supervisorMajor: string) => {
  if (!studentMajor || !supervisorMajor) return false;
  const cleanStudent = studentMajor.trim().toLowerCase();
  const cleanSupervisor = supervisorMajor.trim().toLowerCase();
  if (cleanStudent === cleanSupervisor) return true;
  if (cleanStudent.includes(cleanSupervisor) || cleanSupervisor.includes(cleanStudent)) return true;
  if (cleanSupervisor.includes('ฟิสิกส์') && cleanStudent.includes('ฟิสิกส์')) return true;
  if (cleanSupervisor.includes('เคมี') && cleanStudent.includes('เคมี')) return true;
  if (cleanSupervisor.includes('ชีววิทยา') && cleanStudent.includes('ชีววิทยา')) return true;
  if (cleanSupervisor.includes('คณิต') && cleanStudent.includes('คณิต')) return true;
  if (cleanSupervisor.includes('คอมพิวเตอร์') && cleanStudent.includes('คอมพิวเตอร์')) return true;
  if (cleanSupervisor.includes('ภาษาอังกฤษ') && cleanStudent.includes('ภาษาอังกฤษ')) return true;
  if (cleanSupervisor.includes('ภาษาไทย') && cleanStudent.includes('ภาษาไทย')) return true;
  if (cleanSupervisor.includes('สังคม') && cleanStudent.includes('สังคม')) return true;
  if (cleanSupervisor.includes('ประถม') && cleanStudent.includes('ประถม')) return true;
  if (cleanSupervisor.includes('ปฐมวัย') && cleanStudent.includes('ปฐมวัย')) return true;
  if (cleanSupervisor.includes('ศิลป') && cleanStudent.includes('ศิลป')) return true;
  if (cleanSupervisor.includes('พลศึกษา') && cleanStudent.includes('พลศึกษา')) return true;
  if (cleanSupervisor.includes('ดนตรี') && cleanStudent.includes('ดนตรี')) return true;
  if (cleanSupervisor.includes('อุตสาหกรรม') && cleanStudent.includes('อุตสาหกรรม')) return true;
  return false;
};

const AdminDashboard: React.FC<AdminDashboardProps> = ({ isSupervisorView = false, supervisorMajor }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [effectiveMajor, setEffectiveMajor] = useState<string | undefined>(supervisorMajor);
  const [loading, setLoading] = useState(true);
  const [schoolStats, setSchoolStats] = useState([]);
  const [deanSummary, setDeanSummary] = useState<any>(null);
  const [evalSummary, setEvalSummary] = useState<any[]>([]);
  
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('');

  // Flag to avoid initial duplicate effect trigger
  const initializedRef = useRef(false);

  // Interactive Table & Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMajor, setSelectedMajor] = useState('ALL');
  const [selectedSchool, setSelectedSchool] = useState('ALL');
  const [selectedCourse, setSelectedCourse] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Student Detail Modal
  const [openStudentDetail, setOpenStudentDetail] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [loadingStudentDetail, setLoadingStudentDetail] = useState(false);

  // School Detail Modal
  const [openSchoolDetail, setOpenSchoolDetail] = useState(false);
  const [selectedSchoolData, setSelectedSchoolData] = useState<any>(null);
  const [schoolEvaluations, setSchoolEvaluations] = useState([]);
  const [schoolDetailLoading, setSchoolDetailLoading] = useState(false);

  const tableRef = useRef<HTMLDivElement>(null);

  const fetchData = async (year = selectedYear, semester = selectedSemester, major = effectiveMajor) => {
    try {
      setLoading(true);
      const majorParam = isSupervisorView && major ? major : 'ALL';
      const yearParam = year || 'ALL';
      const semesterParam = semester || 'ALL';

      const [schRes, deanRes, evalRes] = await Promise.all([
        api.get(`/school-evaluations/stats?academic_year=${yearParam}`),
        api.get(`/dean/summary?academic_year=${yearParam}&semester=${semesterParam}&major=${majorParam}`),
        api.get(`/evaluations/admin-eval-summary?academic_year=${yearParam}&semester=${semesterParam}`)
      ]);
      setSchoolStats(schRes.data);
      setDeanSummary(deanRes.data);
      setEvalSummary(evalRes.data);
    } catch (err: any) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const initializeDashboard = async () => {
      try {
        let initialMajor = supervisorMajor;
        if (!initialMajor && isSupervisorView) {
          try {
            const meRes = await api.get('/supervisors/me');
            if (meRes.data?.department) {
              initialMajor = meRes.data.department;
              if (isMounted) setEffectiveMajor(initialMajor);
            }
          } catch (err) {
            console.error('Failed to resolve supervisor department:', err);
          }
        }

        const settingsRes = await api.get('/settings');
        const { current_academic_year, current_semester } = settingsRes.data;
        const initialYear = current_academic_year ? String(current_academic_year) : 'ALL';
        const initialSemester = current_semester ? String(current_semester) : 'ALL';

        if (isMounted) {
          setSelectedYear(initialYear);
          setSelectedSemester(initialSemester);
          initializedRef.current = true;
          await fetchData(initialYear, initialSemester, initialMajor);
        }
      } catch (err) {
        console.error('Failed to initialize dashboard settings:', err);
        if (isMounted) {
          setSelectedYear('ALL');
          setSelectedSemester('ALL');
          initializedRef.current = true;
          await fetchData('ALL', 'ALL', supervisorMajor);
        }
      }
    };

    initializeDashboard();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subsequent filter changes
  useEffect(() => {
    if (initializedRef.current && selectedYear !== '' && selectedSemester !== '') {
      fetchData(selectedYear, selectedSemester, effectiveMajor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedSemester, effectiveMajor]);

  // Handle drill down from KPI cards
  const handleKpiClick = (status: string) => {
    setStatusFilter(status);
    setSearchTerm('');
    setSelectedSchool('ALL');
    if (!isSupervisorView) {
      setSelectedMajor('ALL');
    }
    setPage(0);
    setTimeout(() => {
      tableRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Student list filter logic
  const majorFilteredSummary = isSupervisorView && effectiveMajor
    ? evalSummary.filter((r: any) => isMajorMatch(r.major, effectiveMajor))
    : evalSummary;

  const yearAndSemesterFilteredData = majorFilteredSummary.filter((r: any) => {
    const matchesYear = selectedYear === 'ALL' || String(r.academic_year) === selectedYear;
    const matchesSemester = selectedSemester === 'ALL' || String(r.semester) === selectedSemester;
    return matchesYear && matchesSemester;
  });

  const DEFAULT_MAJORS = Array.from(new Set([
    ...DEFAULT_STUDENT_MAJORS,
    'คณิตศาสตร์ประยุกต์'
  ])).sort((a, b) => a.localeCompare(b, 'th'));

  const uniqueMajors = Array.from(new Set([
    ...DEFAULT_MAJORS,
    ...majorFilteredSummary.map((r: any) => r.major)
  ])).filter(Boolean).sort((a, b) => a.localeCompare(b, 'th'));

  const uniqueSchools = Array.from(new Set([
    ...majorFilteredSummary.map((r: any) => r.school_name),
    ...yearAndSemesterFilteredData.map((r: any) => r.school_name)
  ])).filter(Boolean).sort((a, b) => a.localeCompare(b, 'th'));

  const uniqueCourses = (Array.from(new Set([
    ...majorFilteredSummary.map((r: any) => r.course_code ? `${r.course_code} ${r.course_name || ''}`.trim() : null),
    ...yearAndSemesterFilteredData.map((r: any) => r.course_code ? `${r.course_code} ${r.course_name || ''}`.trim() : null)
  ])).filter(Boolean) as string[]).sort();

  const filteredData = yearAndSemesterFilteredData.filter((r: any) => {
    const fullName = `${r.prefix || ''}${r.first_name} ${r.last_name}`.toLowerCase();
    const studentCode = (r.student_code || '').toLowerCase();
    const major = (r.major || '').toLowerCase();
    const course = r.course_code ? `${r.course_code} ${r.course_name || ''}`.trim() : '';
    const query = searchTerm.toLowerCase();

    const matchesSearch = fullName.includes(query) || studentCode.includes(query) || major.includes(query) || course.toLowerCase().includes(query);
    const matchesMajor = selectedMajor === 'ALL' || r.major === selectedMajor;
    const matchesSchool = selectedSchool === 'ALL' || r.school_name === selectedSchool;
    const matchesCourse = selectedCourse === 'ALL' || course === selectedCourse;

    const isSup = Number(r.requires_supervision) !== 0;
    const supScores = [r.prof_score, r.spec_score, r.co_score].filter(s => s !== null && s !== undefined);
    const supAvg = supScores.length === 3 ? (r.average_score ? parseFloat(r.average_score) : (supScores.reduce((a, b) => a + Number(b), 0) / 3)) : null;

    let matchesStatus = true;
    if (statusFilter === 'PASSED') {
      matchesStatus = r.status === 'PASSED';
    } else if (statusFilter === 'FAILED') {
      matchesStatus = r.status === 'FAILED';
    } else if (statusFilter === 'PENDING') {
      matchesStatus = r.status === 'PENDING';
    } else if (statusFilter === 'NOT_EVALUATED') {
      matchesStatus = r.status === 'NOT_EVALUATED' || (isSup && supScores.length === 0);
    } else if (statusFilter === 'SUP_ALL') {
      matchesStatus = isSup;
    } else if (statusFilter === 'SUP_PASSED') {
      matchesStatus = isSup && supAvg !== null && supAvg >= 60;
    } else if (statusFilter === 'SUP_FAILED') {
      matchesStatus = isSup && supAvg !== null && supAvg < 60;
    } else if (statusFilter === 'SUP_PENDING') {
      matchesStatus = isSup && (supScores.length > 0 && supScores.length < 3);
    } else if (statusFilter === 'SUP_NOT_EVALUATED') {
      matchesStatus = isSup && supScores.length === 0;
    } else if (statusFilter === 'EXEMPT_ALL') {
      matchesStatus = !isSup;
    } else if (statusFilter === 'EXEMPT_EVALUATED') {
      matchesStatus = !isSup && r.workload_score !== null && r.workload_score !== undefined;
    } else if (statusFilter === 'EXEMPT_NOT_EVALUATED') {
      matchesStatus = !isSup && (r.workload_score === null || r.workload_score === undefined);
    }

    return matchesSearch && matchesMajor && matchesSchool && matchesCourse && matchesStatus;
  });

  // Calculate dynamic KPI counts for current selected year & semester
  const kpiTotal = yearAndSemesterFilteredData.length;
  
  // 1. Group: Courses requiring supervision
  const supervisedStudents = yearAndSemesterFilteredData.filter((r: any) => Number(r.requires_supervision) !== 0);
  const supervisedCourseNames = Array.from(new Set(supervisedStudents.map((r: any) => r.course_code ? `${r.course_code} ${r.course_name || ''}`.trim() : null))).filter(Boolean);
  
  const supTotal = supervisedStudents.length;
  const supPassed = supervisedStudents.filter((r: any) => {
    const hasScores = [r.prof_score, r.spec_score, r.co_score].filter(s => s !== null && s !== undefined);
    if (hasScores.length === 3) {
      const avg = r.average_score ? parseFloat(r.average_score) : (hasScores.reduce((a, b) => a + Number(b), 0) / 3);
      return avg >= 60;
    }
    return false;
  }).length;

  const supFailed = supervisedStudents.filter((r: any) => {
    const hasScores = [r.prof_score, r.spec_score, r.co_score].filter(s => s !== null && s !== undefined);
    if (hasScores.length === 3) {
      const avg = r.average_score ? parseFloat(r.average_score) : (hasScores.reduce((a, b) => a + Number(b), 0) / 3);
      return avg < 60;
    }
    return false;
  }).length;

  const supNotEvaluated = supervisedStudents.filter((r: any) => {
    const hasScores = [r.prof_score, r.spec_score, r.co_score].filter(s => s !== null && s !== undefined);
    return hasScores.length === 0;
  }).length;

  const supPending = supTotal - supPassed - supFailed - supNotEvaluated;

  // 2. Group: Courses exempt from supervision
  const exemptStudents = yearAndSemesterFilteredData.filter((r: any) => Number(r.requires_supervision) === 0);
  const exemptCourseNames = Array.from(new Set(exemptStudents.map((r: any) => r.course_code ? `${r.course_code} ${r.course_name || ''}`.trim() : null))).filter(Boolean);

  const exemptTotal = exemptStudents.length;
  const exemptEvaluated = exemptStudents.filter((r: any) => r.workload_score !== null && r.workload_score !== undefined).length;
  const exemptNotEvaluated = exemptStudents.filter((r: any) => r.workload_score === null || r.workload_score === undefined).length;

  const kpiPassed = supPassed;
  const kpiFailed = supFailed;
  const kpiPending = supPending;
  const kpiNotEvaluated = supNotEvaluated + exemptNotEvaluated;
  const schoolsCount = Array.from(new Set(yearAndSemesterFilteredData.map((r: any) => r.school_name).filter(Boolean))).length;

  const handleOpenStudentDetail = async (placementId: string) => {
    if (isSupervisorView) return;
    setLoadingStudentDetail(true);
    setOpenStudentDetail(true);
    try {
      const res = await api.get(`/evaluations/detailed-report/${placementId}`);
      setSelectedReport(res.data);
    } catch (err) {
      console.error('Error fetching detailed evaluation report:', err);
    } finally {
      setLoadingStudentDetail(false);
    }
  };

  const handleOpenSchoolDetail = async (school: any) => {
    setSelectedSchoolData(school);
    setSchoolDetailLoading(true);
    setOpenSchoolDetail(true);
    try {
      const res = await api.get(`/school-evaluations/school/${school.id}`);
      setSchoolEvaluations(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setSchoolDetailLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportTable = () => {
    const headers = ['รหัสนักศึกษา', 'ชื่อ-นามสกุล', 'สาขาวิชา', 'โรงเรียน', 'คะแนนด้านวิชาชีพ (%)', 'คะแนนด้านวิชาเอก (%)', 'คะแนนครูพี่เลี้ยง (%)', 'คะแนนภาระงาน', 'คะแนนเฉลี่ย (%)', 'สถานะ', 'หมายเหตุ'];
    const rows = filteredData.map((r: any) => [
      r.student_code,
      `"${r.prefix || ''}${r.first_name} ${r.last_name}"`,
      `"${r.major}"`,
      `"${r.school_name}"`,
      r.prof_score !== null ? r.prof_score : '-',
      r.spec_score !== null ? r.spec_score : '-',
      r.co_score !== null ? r.co_score : '-',
      r.workload_score !== null && r.workload_score !== undefined ? `${r.workload_score}${r.workload_max_score ? `/${r.workload_max_score}` : ''}` : '-',
      r.average_score || '0.00',
      r.status === 'PASSED' ? 'ผ่าน (PASS)' : (r.status === 'FAILED' ? 'ไม่ผ่าน (FAIL)' : 'รอดำเนินการ'),
      `"${r.remark || '-'}"`
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row: any) => row.join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `tpms_filtered_summary_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportRegistryReport = async () => {
    try {
      const response = await api.get('/evaluations/registry-report');
      const data = response.data;

      const headers = ['รหัสนักศึกษา', 'ชื่อ-นามสกุล', 'สาขาวิชา', 'โรงเรียน', 'คะแนนด้านวิชาชีพ', 'คะแนนด้านวิชาเอก', 'คะแนนครูพี่เลี้ยง', 'คะแนนเฉลี่ย', 'สถานะ', 'หมายเหตุ'];
      const rows = data.map((r: any) => [
        r.student_code,
        `"${r.prefix || ''}${r.first_name} ${r.last_name}"`,
        `"${r.major}"`,
        `"${r.school_name}"`,
        r.prof_score || '-',
        r.spec_score || '-',
        r.co_score || '-',
        r.average_score || '0.00',
        r.status === 'PASSED' ? 'ผ่าน (PASS)' : (r.status === 'FAILED' ? 'ไม่ผ่าน (FAIL)' : 'รอดำเนินการ'),
        `"${r.remark || '-'}"`
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map((row: any) => row.join(','))
      ].join('\n');

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tpms_registry_complete_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
      alert('ไม่สามารถส่งออกรายงานได้');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 10 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="textSecondary">กำลังประมวลผลข้อมูลสถิติ...</Typography>
      </Box>
    );
  }

  // Pie chart data: Group 1 (Requires Supervision)
  const pieDataSup = [
    { name: 'นิเทศครบ / ผ่านเกณฑ์', value: supPassed },
    { name: 'ไม่ผ่านเกณฑ์', value: supFailed },
    { name: 'อยู่ระหว่างการนิเทศ/ประเมินภาระงาน', value: supPending },
    { name: 'ยังไม่ได้รับการนิเทศ/ประเมินภาระงาน', value: supNotEvaluated }
  ].filter(d => d.value > 0);

  const PIE_COLORS_SUP: Record<string, string> = {
    'นิเทศครบ / ผ่านเกณฑ์': '#2e7d32',
    'ไม่ผ่านเกณฑ์': '#d32f2f',
    'อยู่ระหว่างการนิเทศ/ประเมินภาระงาน': '#ed6c02',
    'ยังไม่ได้รับการนิเทศ/ประเมินภาระงาน': '#9e9e9e'
  };

  // Pie chart data: Group 2 (Exempt from Supervision)
  const pieDataExempt = [
    { name: 'ได้รับการประเมินภาระงานแล้ว', value: exemptEvaluated },
    { name: 'ยังไม่ได้รับการประเมินภาระงาน', value: exemptNotEvaluated }
  ].filter(d => d.value > 0);

  const PIE_COLORS_EXEMPT: Record<string, string> = {
    'ได้รับการประเมินภาระงานแล้ว': '#2e7d32',
    'ยังไม่ได้รับการประเมินภาระงาน': '#ed6c02'
  };

  // Competency data mapping
  const competencyData = (deanSummary?.competencyAverages || []).map((c: any) => ({
    name: c.label,
    คะแนนเฉลี่ย: parseFloat(c.value),
    items: c.items || []
  }));

  return (
    <Box sx={{ py: 1 }}>
      {/* Executive Hero Analytics Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' }, 
        gap: 2.5,
        mb: 4 
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Avatar sx={{ bgcolor: 'rgba(30, 64, 175, 0.1)', color: '#1E40AF', width: 42, height: 42 }}>
              <AssessmentIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography 
                variant="overline" 
                sx={{ 
                  color: '#1E40AF', 
                  fontWeight: 800, 
                  letterSpacing: '0.08em', 
                  display: 'block', 
                  lineHeight: 1.1 
                }}
              >
                EXECUTIVE ANALYTICS & MONITORING
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 800, 
                  color: '#0F172A', 
                  fontFamily: '"Prompt", sans-serif',
                  letterSpacing: '-0.02em'
                }}
              >
                แดชบอร์ดข้อมูลรายงานผล (System Dashboard)
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ color: '#64748B', ml: { xs: 0, sm: 7 } }}>
            ภาพรวมผลการฝึกประสบการณ์วิชาชีพครูและการประเมินสมรรถนะ คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', md: 'auto' }, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 150, width: { xs: '100%', md: 'auto' } }} size="small">
            <InputLabel id="admin-semester-filter-label">ภาคการเรียน</InputLabel>
            <Select
              labelId="admin-semester-filter-label"
              value={selectedSemester}
              label="ภาคการเรียน"
              onChange={(e) => {
                setSelectedSemester(e.target.value);
                setPage(0);
              }}
              sx={{ borderRadius: 2.5, bgcolor: '#ffffff' }}
            >
              <MenuItem value="ALL">ทุกภาคเรียน</MenuItem>
              <MenuItem value="1">ภาคเรียนที่ 1</MenuItem>
              <MenuItem value="2">ภาคเรียนที่ 2</MenuItem>
              <MenuItem value="3">ภาคเรียนที่ 3</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150, width: { xs: '100%', md: 'auto' } }} size="small">
            <InputLabel id="admin-year-filter-label">ปีการศึกษา</InputLabel>
            <Select
              labelId="admin-year-filter-label"
              value={selectedYear}
              label="ปีการศึกษา"
              onChange={(e) => {
                setSelectedYear(e.target.value);
                setPage(0);
              }}
              sx={{ borderRadius: 2.5, bgcolor: '#ffffff' }}
            >
              <MenuItem value="ALL">ทุกปีการศึกษา</MenuItem>
              {(deanSummary?.academicYears || []).map((y: any) => (
                <MenuItem key={y} value={String(y)}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="contained" 
            color="success" 
            startIcon={<DownloadIcon />}
            onClick={handleExportRegistryReport}
            fullWidth={isMobile}
            sx={{ 
              borderRadius: 2.5, 
              fontWeight: 700, 
              fontFamily: '"Prompt", sans-serif',
              boxShadow: '0 4px 14px rgba(5, 150, 105, 0.25)',
              px: 2.5,
              py: 0.9,
              textTransform: 'none'
            }}
          >
            ส่งออกฝ่ายทะเบียน (CSV)
          </Button>
        </Box>
      </Box>

      {/* 4-Card Executive KPI Metric Deck */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        {/* KPI 1: Total All Practicum Students */}
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <GlassCard
            highlightColor="#1E40AF"
            sx={{
              p: 2.5,
              height: '100%',
              cursor: 'pointer',
              border: statusFilter === 'ALL' ? '2px solid #1E40AF' : undefined,
              bgcolor: statusFilter === 'ALL' ? 'rgba(239, 246, 255, 0.85)' : undefined,
              '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(30, 64, 175, 0.12)' },
            }}
            onClick={() => handleKpiClick('ALL')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Avatar sx={{ bgcolor: 'rgba(30, 64, 175, 0.1)', color: '#1E40AF', width: 44, height: 44 }}>
                <PeopleIcon />
              </Avatar>
              <Chip
                label={statusFilter === 'ALL' ? 'กำลังเลือก: ทั้งหมด' : 'คลิกดูทั้งหมด'}
                color={statusFilter === 'ALL' ? 'primary' : 'default'}
                size="small"
                sx={{ fontWeight: 700, fontSize: '0.7rem' }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block' }}>
              นักศึกษาฝึกสอนทั้งหมด
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1E40AF', fontFamily: '"Prompt", sans-serif', my: 0.5 }}>
              {kpiTotal} <Typography component="span" variant="body2" sx={{ color: '#64748B', fontWeight: 600 }}>คน</Typography>
            </Typography>
            <Typography variant="caption" sx={{ color: '#475569', display: 'block' }}>
              ทุกหลักสูตรในภาคเรียนนี้
            </Typography>
          </GlassCard>
        </Grid>

        {/* KPI 2: Supervised Practicum Group */}
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <GlassCard
            highlightColor="#2563EB"
            sx={{
              p: 2.5,
              height: '100%',
              cursor: 'pointer',
              border: statusFilter === 'SUP_ALL' ? '2px solid #2563EB' : undefined,
              bgcolor: statusFilter === 'SUP_ALL' ? 'rgba(239, 246, 255, 0.85)' : undefined,
              '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(37, 99, 235, 0.12)' },
            }}
            onClick={() => handleKpiClick('SUP_ALL')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Avatar sx={{ bgcolor: 'rgba(37, 99, 235, 0.1)', color: '#2563EB', width: 44, height: 44 }}>
                <CourseIcon />
              </Avatar>
              <Chip
                label={statusFilter === 'SUP_ALL' ? 'กำลังเลือก' : 'วิชาที่ต้องนิเทศ'}
                color={statusFilter === 'SUP_ALL' ? 'primary' : 'default'}
                size="small"
                sx={{ fontWeight: 700, fontSize: '0.7rem' }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block' }}>
              วิชาที่ต้องรับการนิเทศ
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#2563EB', fontFamily: '"Prompt", sans-serif', my: 0.5 }}>
              {supTotal} <Typography component="span" variant="body2" sx={{ color: '#64748B', fontWeight: 600 }}>คน</Typography>
            </Typography>
            <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700, display: 'block' }}>
              ผ่านเกณฑ์ {supPassed} คน ({supTotal > 0 ? Math.round((supPassed / supTotal) * 100) : 0}%)
            </Typography>
          </GlassCard>
        </Grid>

        {/* KPI 3: Exempt Practicum Group */}
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <GlassCard
            highlightColor="#D97706"
            sx={{
              p: 2.5,
              height: '100%',
              cursor: 'pointer',
              border: statusFilter === 'EXEMPT_ALL' ? '2px solid #D97706' : undefined,
              bgcolor: statusFilter === 'EXEMPT_ALL' ? 'rgba(254, 243, 199, 0.5)' : undefined,
              '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(217, 119, 6, 0.12)' },
            }}
            onClick={() => handleKpiClick('EXEMPT_ALL')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Avatar sx={{ bgcolor: 'rgba(217, 119, 6, 0.1)', color: '#D97706', width: 44, height: 44 }}>
                <AssignmentIcon />
              </Avatar>
              <Chip
                label={statusFilter === 'EXEMPT_ALL' ? 'กำลังเลือก' : 'ยกเว้นการนิเทศ'}
                color={statusFilter === 'EXEMPT_ALL' ? 'warning' : 'default'}
                size="small"
                sx={{ fontWeight: 700, fontSize: '0.7rem' }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block' }}>
              วิชาที่ไม่ต้องรับการนิเทศ
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#D97706', fontFamily: '"Prompt", sans-serif', my: 0.5 }}>
              {exemptTotal} <Typography component="span" variant="body2" sx={{ color: '#64748B', fontWeight: 600 }}>คน</Typography>
            </Typography>
            <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700, display: 'block' }}>
              ประเมินภาระงานแล้ว {exemptEvaluated} คน ({exemptTotal > 0 ? Math.round((exemptEvaluated / exemptTotal) * 100) : 0}%)
            </Typography>
          </GlassCard>
        </Grid>

        {/* KPI 4: Partner Schools */}
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <GlassCard
            highlightColor="#059669"
            sx={{
              p: 2.5,
              height: '100%',
              '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(5, 150, 105, 0.12)' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Avatar sx={{ bgcolor: 'rgba(5, 150, 105, 0.1)', color: '#059669', width: 44, height: 44 }}>
                <SchoolIcon />
              </Avatar>
              <Chip
                label="สถานศึกษาเครือข่าย"
                size="small"
                sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: 'rgba(5, 150, 105, 0.08)', color: '#059669' }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block' }}>
              สถานศึกษาที่นักศึกษาออกฝึก
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#059669', fontFamily: '"Prompt", sans-serif', my: 0.5 }}>
              {schoolsCount || (schoolStats?.length || 0)} <Typography component="span" variant="body2" sx={{ color: '#64748B', fontWeight: 600 }}>แห่ง</Typography>
            </Typography>
            <Typography variant="caption" sx={{ color: '#475569', display: 'block' }}>
              โรงเรียนฝึกประสบการณ์วิชาชีพ
            </Typography>
          </GlassCard>
        </Grid>
      </Grid>

      {/* 2. Group: Courses Requiring Supervision (กลุ่มวิชาที่ต้องรับการนิเทศ) */}
      <GlassCard highlightColor="#1E40AF" sx={{ p: { xs: 2.5, sm: 3 }, mb: 3.5 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'rgba(30, 64, 175, 0.1)', color: '#1E40AF', width: 40, height: 40 }}>
              <CourseIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1E40AF', fontFamily: '"Prompt", sans-serif' }}>
                กลุ่มวิชาที่ต้องรับการนิเทศ (Supervised Practicum)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap', mt: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B' }}>
                  ได้แก่วิชา:
                </Typography>
                {supervisedCourseNames.length > 0 ? (
                  supervisedCourseNames.map((cName) => (
                    <Chip key={cName} label={cName} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.72rem', height: 22, bgcolor: '#ffffff' }} />
                  ))
                ) : (
                  <Typography variant="caption" color="text.secondary">ไม่พบรายวิชาในภาคเรียนนี้</Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Chip label={`รวม ${supTotal} คน`} color="primary" sx={{ fontWeight: 800, fontFamily: '"Prompt", sans-serif' }} />
        </Box>

        <Grid container spacing={2}>
          {/* Sup Card 1: นักศึกษาที่ต้องได้รับการนิเทศ ทั้งหมด */}
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card 
              sx={{ 
                p: 2, 
                borderRadius: 2.5, 
                cursor: 'pointer',
                transition: '0.2s',
                bgcolor: statusFilter === 'SUP_ALL' ? 'rgba(239, 246, 255, 0.9)' : '#ffffff',
                border: '1.5px solid',
                borderColor: statusFilter === 'SUP_ALL' ? '#1E40AF' : '#E2E8F0',
                boxShadow: statusFilter === 'SUP_ALL' ? '0 4px 14px rgba(30, 64, 175, 0.15)' : 'none',
                '&:hover': { transform: 'translateY(-3px)', borderColor: '#1E40AF' }
              }}
              onClick={() => handleKpiClick('SUP_ALL')}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B', display: 'block' }}>
                นักศึกษาที่ต้องได้รับการนิเทศ
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#1E40AF', fontFamily: '"Prompt", sans-serif', my: 0.5 }}>
                {supTotal}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B' }}>
                วิชาที่ต้องรับการนิเทศทั้งหมด
              </Typography>
            </Card>
          </Grid>

          {/* Sup Card 2: นิเทศครบ / ผ่านเกณฑ์ */}
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card 
              sx={{ 
                p: 2, 
                borderRadius: 2.5, 
                cursor: 'pointer',
                transition: '0.2s',
                bgcolor: statusFilter === 'SUP_PASSED' ? 'rgba(236, 253, 245, 0.9)' : '#ffffff',
                border: '1.5px solid',
                borderColor: statusFilter === 'SUP_PASSED' ? '#059669' : '#E2E8F0',
                boxShadow: statusFilter === 'SUP_PASSED' ? '0 4px 14px rgba(5, 150, 105, 0.15)' : 'none',
                '&:hover': { transform: 'translateY(-3px)', borderColor: '#059669' }
              }}
              onClick={() => handleKpiClick('SUP_PASSED')}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B', display: 'block' }}>
                นิเทศครบ / ผ่านเกณฑ์
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#059669', fontFamily: '"Prompt", sans-serif', my: 0.5 }}>
                {supPassed}
              </Typography>
              <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600 }}>
                คะแนนเฉลี่ย ≥ 60%
              </Typography>
            </Card>
          </Grid>

          {/* Sup Card 3: ไม่ผ่านเกณฑ์ */}
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card 
              sx={{ 
                p: 2, 
                borderRadius: 2.5, 
                cursor: 'pointer',
                transition: '0.2s',
                bgcolor: statusFilter === 'SUP_FAILED' ? 'rgba(254, 242, 242, 0.9)' : '#ffffff',
                border: '1.5px solid',
                borderColor: statusFilter === 'SUP_FAILED' ? '#DC2626' : '#E2E8F0',
                boxShadow: statusFilter === 'SUP_FAILED' ? '0 4px 14px rgba(220, 38, 38, 0.15)' : 'none',
                '&:hover': { transform: 'translateY(-3px)', borderColor: '#DC2626' }
              }}
              onClick={() => handleKpiClick('SUP_FAILED')}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B', display: 'block' }}>
                ไม่ผ่านเกณฑ์
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#DC2626', fontFamily: '"Prompt", sans-serif', my: 0.5 }}>
                {supFailed}
              </Typography>
              <Typography variant="caption" sx={{ color: '#DC2626', fontWeight: 600 }}>
                คะแนนเฉลี่ย &lt; 60%
              </Typography>
            </Card>
          </Grid>

          {/* Sup Card 4: อยู่ระหว่างการนิเทศ/ประเมินภาระงาน */}
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card 
              sx={{ 
                p: 2, 
                borderRadius: 2.5, 
                cursor: 'pointer',
                transition: '0.2s',
                bgcolor: statusFilter === 'SUP_PENDING' ? 'rgba(254, 243, 199, 0.6)' : '#ffffff',
                border: '1.5px solid',
                borderColor: statusFilter === 'SUP_PENDING' ? '#D97706' : '#E2E8F0',
                boxShadow: statusFilter === 'SUP_PENDING' ? '0 4px 14px rgba(217, 119, 6, 0.15)' : 'none',
                '&:hover': { transform: 'translateY(-3px)', borderColor: '#D97706' }
              }}
              onClick={() => handleKpiClick('SUP_PENDING')}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B', display: 'block' }}>
                อยู่ระหว่างการนิเทศ
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#D97706', fontFamily: '"Prompt", sans-serif', my: 0.5 }}>
                {supPending}
              </Typography>
              <Typography variant="caption" sx={{ color: '#D97706' }}>
                ประเมินบางส่วนแต่ยังไม่ครบ
              </Typography>
            </Card>
          </Grid>

          {/* Sup Card 5: ยังไม่ได้รับการนิเทศ/ประเมินภาระงาน */}
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card 
              sx={{ 
                p: 2, 
                borderRadius: 2.5, 
                cursor: 'pointer',
                transition: '0.2s',
                bgcolor: statusFilter === 'SUP_NOT_EVALUATED' ? '#F1F5F9' : '#ffffff',
                border: '1.5px solid',
                borderColor: statusFilter === 'SUP_NOT_EVALUATED' ? '#64748B' : '#E2E8F0',
                boxShadow: statusFilter === 'SUP_NOT_EVALUATED' ? '0 4px 14px rgba(100, 116, 139, 0.15)' : 'none',
                '&:hover': { transform: 'translateY(-3px)', borderColor: '#64748B' }
              }}
              onClick={() => handleKpiClick('SUP_NOT_EVALUATED')}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B', display: 'block' }}>
                ยังไม่ได้รับการนิเทศ
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#64748B', fontFamily: '"Prompt", sans-serif', my: 0.5 }}>
                {supNotEvaluated}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B' }}>
                ยังไม่มีการบันทึกคะแนน
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </GlassCard>

      {/* 3. Group: Courses Exempt from Supervision (กลุ่มวิชาที่ไม่ต้องรับการนิเทศ) */}
      <GlassCard highlightColor="#D97706" sx={{ p: { xs: 2.5, sm: 3 }, mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'rgba(217, 119, 6, 0.1)', color: '#D97706', width: 40, height: 40 }}>
              <AssignmentIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#9A3412', fontFamily: '"Prompt", sans-serif' }}>
                กลุ่มวิชาที่ไม่ต้องรับการนิเทศ (Exempt Practicum - ประเมินภาระงาน)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap', mt: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B' }}>
                  ได้แก่วิชา:
                </Typography>
                {exemptCourseNames.length > 0 ? (
                  exemptCourseNames.map((cName) => (
                    <Chip key={cName} label={cName} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.72rem', height: 22, borderColor: '#FED7AA', bgcolor: '#ffffff' }} />
                  ))
                ) : (
                  <Typography variant="caption" color="text.secondary">ไม่พบรายวิชาในภาคเรียนนี้</Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Chip label={`รวม ${exemptTotal} คน`} color="warning" sx={{ fontWeight: 800, fontFamily: '"Prompt", sans-serif' }} />
        </Box>

        <Grid container spacing={2}>
          {/* Exempt Card 1: นักศึกษาในกลุ่มวิชาที่ไม่ต้องรับการนิเทศ */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card 
              sx={{ 
                p: 2, 
                borderRadius: 2.5, 
                cursor: 'pointer',
                transition: '0.2s',
                bgcolor: statusFilter === 'EXEMPT_ALL' ? 'rgba(254, 243, 199, 0.6)' : '#ffffff',
                border: '1.5px solid',
                borderColor: statusFilter === 'EXEMPT_ALL' ? '#D97706' : '#E2E8F0',
                boxShadow: statusFilter === 'EXEMPT_ALL' ? '0 4px 14px rgba(217, 119, 6, 0.15)' : 'none',
                '&:hover': { transform: 'translateY(-3px)', borderColor: '#D97706' }
              }}
              onClick={() => handleKpiClick('EXEMPT_ALL')}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B', display: 'block' }}>
                นักศึกษาในกลุ่มวิชาที่ไม่ต้องรับการนิเทศ
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#D97706', fontFamily: '"Prompt", sans-serif', my: 0.5 }}>
                {exemptTotal}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B' }}>
                วิชาที่ยกเว้นการนิเทศทั้งหมด
              </Typography>
            </Card>
          </Grid>

          {/* Exempt Card 2: ยังไม่ได้รับการประเมินภาระงาน */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card 
              sx={{ 
                p: 2, 
                borderRadius: 2.5, 
                cursor: 'pointer',
                transition: '0.2s',
                bgcolor: statusFilter === 'EXEMPT_NOT_EVALUATED' ? 'rgba(254, 243, 199, 0.6)' : '#ffffff',
                border: '1.5px solid',
                borderColor: statusFilter === 'EXEMPT_NOT_EVALUATED' ? '#D97706' : '#E2E8F0',
                boxShadow: statusFilter === 'EXEMPT_NOT_EVALUATED' ? '0 4px 14px rgba(217, 119, 6, 0.15)' : 'none',
                '&:hover': { transform: 'translateY(-3px)', borderColor: '#D97706' }
              }}
              onClick={() => handleKpiClick('EXEMPT_NOT_EVALUATED')}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B', display: 'block' }}>
                ยังไม่ได้รับการประเมินภาระงาน
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#D97706', fontFamily: '"Prompt", sans-serif', my: 0.5 }}>
                {exemptNotEvaluated}
              </Typography>
              <Typography variant="caption" sx={{ color: '#D97706' }}>
                รออาจารย์นิเทศก์ประเมินภาระงาน
              </Typography>
            </Card>
          </Grid>

          {/* Exempt Card 3: ได้รับการประเมินภาระงานแล้ว */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card 
              sx={{ 
                p: 2, 
                borderRadius: 2.5, 
                cursor: 'pointer',
                transition: '0.2s',
                bgcolor: statusFilter === 'EXEMPT_EVALUATED' ? 'rgba(236, 253, 245, 0.9)' : '#ffffff',
                border: '1.5px solid',
                borderColor: statusFilter === 'EXEMPT_EVALUATED' ? '#059669' : '#E2E8F0',
                boxShadow: statusFilter === 'EXEMPT_EVALUATED' ? '0 4px 14px rgba(5, 150, 105, 0.15)' : 'none',
                '&:hover': { transform: 'translateY(-3px)', borderColor: '#059669' }
              }}
              onClick={() => handleKpiClick('EXEMPT_EVALUATED')}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B', display: 'block' }}>
                ได้รับการประเมินภาระงานแล้ว
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#059669', fontFamily: '"Prompt", sans-serif', my: 0.5 }}>
                {exemptEvaluated}
              </Typography>
              <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600 }}>
                บันทึกคะแนนภาระงานเรียบร้อย
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </GlassCard>
      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Chart 1: Proportions for Supervised Group */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper 
            sx={{ 
              p: 3, 
              borderRadius: 3.5, 
              border: '1px solid rgba(226, 232, 240, 0.85)',
              boxShadow: '0 8px 30px rgba(31, 38, 135, 0.05)', 
              height: 430, 
              display: 'flex', 
              flexDirection: 'column' 
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1E40AF', fontFamily: '"Prompt", sans-serif' }}>
                สัดส่วนผลการประเมิน: กลุ่มวิชาที่ต้องรับการนิเทศ
              </Typography>
              <Chip label="Supervised" size="small" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: 'rgba(30, 64, 175, 0.08)', color: '#1E40AF' }} />
            </Box>
            <Divider sx={{ my: 1.5 }} />
            {pieDataSup.length > 0 ? (
              <Box sx={{ flexGrow: 1, minHeight: 300, height: 300 }}>
                <TypedResponsiveContainer width="100%" height={300} minWidth={0} minHeight={300}>
                  <TypedPieChart>
                    <TypedPie
                      data={pieDataSup}
                      cx="50%"
                      cy="50%"
                      innerRadius={isMobile ? 45 : 65}
                      outerRadius={isMobile ? 75 : 100}
                      paddingAngle={4}
                      dataKey="value"
                      label
                    >
                      {pieDataSup.map((entry: any, index: number) => (
                        <Cell key={`cell-sup-${index}`} fill={PIE_COLORS_SUP[entry.name] || CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </TypedPie>
                    <TypedTooltip />
                  </TypedPieChart>
                </TypedResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="textSecondary" variant="caption">ไม่มีข้อมูลการประเมินในกลุ่มวิชานี้</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Chart 2: Proportions for Exempt Group */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper 
            sx={{ 
              p: 3, 
              borderRadius: 3.5, 
              border: '1px solid rgba(226, 232, 240, 0.85)',
              boxShadow: '0 8px 30px rgba(31, 38, 135, 0.05)', 
              height: 430, 
              display: 'flex', 
              flexDirection: 'column' 
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#9A3412', fontFamily: '"Prompt", sans-serif' }}>
                สัดส่วนการประเมินภาระงาน: กลุ่มวิชาที่ไม่ต้องรับการนิเทศ
              </Typography>
              <Chip label="Exempt" size="small" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: 'rgba(217, 119, 6, 0.08)', color: '#D97706' }} />
            </Box>
            <Divider sx={{ my: 1.5 }} />
            {pieDataExempt.length > 0 ? (
              <Box sx={{ flexGrow: 1, minHeight: 300, height: 300 }}>
                <TypedResponsiveContainer width="100%" height={300} minWidth={0} minHeight={300}>
                  <TypedPieChart>
                    <TypedPie
                      data={pieDataExempt}
                      cx="50%"
                      cy="50%"
                      innerRadius={isMobile ? 45 : 65}
                      outerRadius={isMobile ? 75 : 100}
                      paddingAngle={4}
                      dataKey="value"
                      label
                    >
                      {pieDataExempt.map((entry: any, index: number) => (
                        <Cell key={`cell-ex-${index}`} fill={PIE_COLORS_EXEMPT[entry.name] || CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </TypedPie>
                    <TypedTooltip />
                  </TypedPieChart>
                </TypedResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="textSecondary" variant="caption">ไม่มีข้อมูลการประเมินในกลุ่มวิชานี้</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Bar Chart: Competencies */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper 
            sx={{ 
              p: 3, 
              borderRadius: 3.5, 
              border: '1px solid rgba(226, 232, 240, 0.85)',
              boxShadow: '0 8px 30px rgba(31, 38, 135, 0.05)', 
              height: 410, 
              display: 'flex', 
              flexDirection: 'column' 
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800, fontFamily: '"Prompt", sans-serif' }}>
              คะแนนเฉลี่ยตามสมรรถนะ (8 ด้านคุรุสภา)
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            {competencyData.length > 0 ? (
              <Box sx={{ flexGrow: 1, minHeight: 280, height: 280 }}>
                <TypedResponsiveContainer width="100%" height={280} minWidth={0} minHeight={280}>
                  <TypedBarChart data={competencyData}>
                    <TypedCartesianGrid strokeDasharray="3 3" />
                    <TypedXAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <TypedYAxis domain={[0, 4]} />
                    <TypedTooltip content={<CustomTooltip />} />
                    <TypedBar dataKey="คะแนนเฉลี่ย" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                  </TypedBarChart>
                </TypedResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="textSecondary" variant="caption">ไม่มีข้อมูลสมรรถนะเพื่อประมวลผล</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Network School Ratings (Horizontal Bar) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper 
            sx={{ 
              p: 3, 
              borderRadius: 3.5, 
              border: '1px solid rgba(226, 232, 240, 0.85)',
              boxShadow: '0 8px 30px rgba(31, 38, 135, 0.05)', 
              height: 410, 
              display: 'flex', 
              flexDirection: 'column' 
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800, fontFamily: '"Prompt", sans-serif' }}>
              การประเมินคุณภาพสถานศึกษา (คลิกแท่งกราฟเพื่อดูรายละเอียด)
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            {schoolStats.length > 0 ? (
              <Box sx={{ flexGrow: 1, minHeight: 300, height: 300 }}>
                <TypedResponsiveContainer width="100%" height={300} minWidth={0} minHeight={300}>
                  <TypedBarChart data={schoolStats || []} layout="vertical" margin={{ left: 10, right: 10 }}>
                    <TypedCartesianGrid strokeDasharray="3 3" />
                    <TypedXAxis type="number" domain={[0, 5]} />
                    <TypedYAxis dataKey="name" type="category" width={isMobile ? 100 : 150} tick={{ fontSize: 10 }} />
                    <TypedTooltip />
                    <TypedBar 
                      dataKey="avg_rating" 
                      name="คะแนนเฉลี่ย" 
                      fill="#059669" 
                      radius={[0, 4, 4, 0]} 
                      onClick={(e: any) => handleOpenSchoolDetail(e)} 
                      style={{ cursor: 'pointer' }}
                    />
                  </TypedBarChart>
                </TypedResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="textSecondary" variant="caption">ยังไม่มีบันทึกข้อมูลคะแนนประเมินโรงเรียน</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Student List Table & Filters Section */}
      <Box ref={tableRef} sx={{ mt: 5 }}>
        <Paper 
          sx={{ 
            p: { xs: 2.5, sm: 3.5 }, 
            borderRadius: 3.5, 
            border: '1px solid rgba(226, 232, 240, 0.85)',
            boxShadow: '0 8px 30px rgba(31, 38, 135, 0.05)' 
          }}
        >
          {/* Table Header with Quick Filters */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', md: 'center' }, 
            gap: 2, 
            mb: 3
          }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: '"Prompt", sans-serif', color: '#0F172A' }}>
                รายละเอียดผลการประเมินนักศึกษา (Student Evaluation Registry)
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                แสดงผลลัพธ์การคัดกรองทั้งหมด {filteredData.length} คน
              </Typography>
            </Box>
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<DownloadIcon />}
              onClick={handleExportTable}
              disabled={filteredData.length === 0}
              sx={{ borderRadius: 2.5, fontWeight: 700, fontFamily: '"Prompt", sans-serif', textTransform: 'none' }}
            >
              ส่งออกตารางนี้ (CSV)
            </Button>
          </Box>

          {/* Quick Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="ค้นหาอย่างรวดเร็วด้วยชื่อ-นามสกุล, รหัสนักศึกษา หรือโปรแกรมวิชา (สาขาวิชา)..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            sx={{ 
              mb: 3, 
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                backgroundColor: '#F8FAFC'
              }
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }
            }}
          />

          {/* Filters Bar */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="course-filter-label">คัดกรองรายวิชา</InputLabel>
                <Select
                  labelId="course-filter-label"
                  value={selectedCourse}
                  label="คัดกรองรายวิชา"
                  onChange={(e) => {
                    setSelectedCourse(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: 2.5, bgcolor: '#ffffff' }}
                >
                  <MenuItem value="ALL">รายวิชาทั้งหมด</MenuItem>
                  {uniqueCourses.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="major-filter-label">คัดกรองสาขาวิชา</InputLabel>
                <Select
                  labelId="major-filter-label"
                  value={selectedMajor}
                  label="คัดกรองสาขาวิชา"
                  onChange={(e) => {
                    setSelectedMajor(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: 2.5, bgcolor: '#ffffff' }}
                >
                  <MenuItem value="ALL">สาขาวิชาทั้งหมด</MenuItem>
                  {uniqueMajors.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="school-filter-label">คัดกรองโรงเรียน</InputLabel>
                <Select
                  labelId="school-filter-label"
                  value={selectedSchool}
                  label="คัดกรองโรงเรียน"
                  onChange={(e) => {
                    setSelectedSchool(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: 2.5, bgcolor: '#ffffff' }}
                >
                  <MenuItem value="ALL">โรงเรียนทั้งหมด</MenuItem>
                  {uniqueSchools.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-filter-label">สถานะการประเมิน</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  label="สถานะการประเมิน"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: 2.5, bgcolor: '#ffffff' }}
                >
                  <MenuItem value="ALL">ทุกสถานะ ({kpiTotal} คน)</MenuItem>
                  <MenuItem value="SUP_ALL">กลุ่มวิชาที่ต้องรับการนิเทศ ทั้งหมด ({supTotal} คน)</MenuItem>
                  <MenuItem value="SUP_PASSED">กลุ่มวิชาที่ต้องรับการนิเทศ: นิเทศครบ / ผ่านเกณฑ์ ({supPassed} คน)</MenuItem>
                  <MenuItem value="SUP_FAILED">กลุ่มวิชาที่ต้องรับการนิเทศ: ไม่ผ่านเกณฑ์ ({supFailed} คน)</MenuItem>
                  <MenuItem value="SUP_PENDING">กลุ่มวิชาที่ต้องรับการนิเทศ: อยู่ระหว่างการนิเทศ ({supPending} คน)</MenuItem>
                  <MenuItem value="SUP_NOT_EVALUATED">กลุ่มวิชาที่ต้องรับการนิเทศ: ยังไม่ได้รับการนิเทศ ({supNotEvaluated} คน)</MenuItem>
                  <MenuItem value="EXEMPT_ALL">กลุ่มวิชาที่ไม่ต้องรับการนิเทศ ทั้งหมด ({exemptTotal} คน)</MenuItem>
                  <MenuItem value="EXEMPT_EVALUATED">กลุ่มวิชาที่ไม่ต้องรับการนิเทศ: ประเมินแล้ว ({exemptEvaluated} คน)</MenuItem>
                  <MenuItem value="EXEMPT_NOT_EVALUATED">กลุ่มวิชาที่ไม่ต้องรับการนิเทศ: ยังไม่ประเมิน ({exemptNotEvaluated} คน)</MenuItem>
                  <MenuItem value="PASSED">ผ่านเกณฑ์สมรรถนะ (PASSED) ({kpiPassed} คน)</MenuItem>
                  <MenuItem value="FAILED">ไม่ผ่านเกณฑ์สมรรถนะ (FAILED) ({kpiFailed} คน)</MenuItem>
                  <MenuItem value="PENDING">อยู่ระหว่างการประเมิน (PENDING) ({kpiPending} คน)</MenuItem>
                  <MenuItem value="NOT_EVALUATED">ยังไม่ได้รับการประเมินเลย ({kpiNotEvaluated} คน)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Reset Filters Link */}
          {(searchTerm || selectedMajor !== 'ALL' || selectedSchool !== 'ALL' || selectedCourse !== 'ALL' || statusFilter !== 'ALL') && (
            <Box sx={{ mb: 2 }}>
              <Button 
                size="small" 
                color="secondary" 
                onClick={() => {
                  setSearchTerm('');
                  setSelectedMajor('ALL');
                  setSelectedSchool('ALL');
                  setSelectedCourse('ALL');
                  setStatusFilter('ALL');
                  setPage(0);
                }}
                sx={{ fontWeight: 'bold' }}
              >
                ล้างตัวกรองทั้งหมด
              </Button>
            </Box>
          )}

          {/* Table Container */}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700, py: 2 }}>นักศึกษา / ข้อมูลทั่วไป</TableCell>
                  <TableCell sx={{ fontWeight: 700, py: 2 }}>โรงเรียนฝึกประสบการณ์</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 2 }}>ด้านวิชาชีพครู</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 2 }}>ด้านวิชาเอก</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 2 }}>ครูพี่เลี้ยง</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 2 }}>ภาระงาน</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 2 }}>คะแนนเฉลี่ย</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 2 }}>สถานะ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="textSecondary" sx={{ fontWeight: 600 }}>ไม่พบข้อมูลนักศึกษาที่เข้าเงื่อนไข</Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>กรุณาปรับเงื่อนไขตัวกรอง หรือขยายการค้นหาของคุณ</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r: any) => (
                    <TableRow 
                      key={r.placement_id} 
                      hover={!isSupervisorView} 
                      onClick={isSupervisorView ? undefined : () => handleOpenStudentDetail(r.placement_id)} 
                      sx={{ 
                        cursor: isSupervisorView ? 'default' : 'pointer',
                        '&:hover': {
                          backgroundColor: isSupervisorView ? 'inherit' : 'rgba(25, 118, 210, 0.02) !important'
                        }
                      }}
                    >
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          {r.prefix || ''}{r.first_name} {r.last_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontWeight: 500 }}>
                          รหัส: {r.student_code} | สาขา: {r.major}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.school_name}</Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>ปีการศึกษา {r.academic_year} (ภาคเรียนที่ {r.semester})</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2 }}>
                        {Number(r.requires_supervision) === 0 ? (
                          <Chip label="ยกเว้นการนิเทศ" size="small" variant="outlined" sx={{ color: 'text.secondary', fontSize: '0.72rem' }} />
                        ) : r.prof_score !== null ? (
                          <Chip label={`${r.prof_score}%`} size="small" sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)', color: '#1976d2', fontWeight: 'bold' }} />
                        ) : (
                          <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>ยังไม่ประเมิน</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2 }}>
                        {Number(r.requires_supervision) === 0 ? (
                          <Chip label="ยกเว้นการนิเทศ" size="small" variant="outlined" sx={{ color: 'text.secondary', fontSize: '0.72rem' }} />
                        ) : r.spec_score !== null ? (
                          <Chip label={`${r.spec_score}%`} size="small" sx={{ bgcolor: 'rgba(156, 39, 176, 0.08)', color: '#9c27b0', fontWeight: 'bold' }} />
                        ) : (
                          <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>ยังไม่ประเมิน</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2 }}>
                        {Number(r.requires_supervision) === 0 ? (
                          <Chip label="ยกเว้นการนิเทศ" size="small" variant="outlined" sx={{ color: 'text.secondary', fontSize: '0.72rem' }} />
                        ) : r.co_score !== null ? (
                          <Chip label={`${r.co_score}%`} size="small" sx={{ bgcolor: 'rgba(237, 108, 2, 0.08)', color: '#ed6c02', fontWeight: 'bold' }} />
                        ) : (
                          <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>ยังไม่ประเมิน</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2 }}>
                        {r.workload_score !== null && r.workload_score !== undefined ? (
                          <Chip 
                            label={`${r.workload_score}${r.workload_max_score ? ` / ${r.workload_max_score}` : ''}`} 
                            size="small" 
                            sx={{ bgcolor: 'rgba(2, 136, 209, 0.08)', color: '#0288d1', fontWeight: 'bold' }} 
                          />
                        ) : (
                          <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>ยังไม่ประเมิน</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2 }}>
                        {r.requires_supervision === 0 ? (
                          <Chip label="ยกเว้นการนิเทศ" size="small" variant="outlined" sx={{ color: 'text.secondary', fontSize: '0.72rem' }} />
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main', fontSize: '1rem' }}>
                            {r.average_score ? `${parseFloat(r.average_score).toFixed(1)}%` : '-'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2 }}>
                        {(() => {
                          const isExempt = Number(r.requires_supervision) === 0;
                          if (isExempt) {
                            if (r.workload_score !== null && r.workload_score !== undefined) {
                              const max = Number(r.workload_max_score) || 100;
                              const pct = max > 0 ? (Number(r.workload_score) / max) * 100 : 0;
                              return pct >= 60 ? (
                                <Chip icon={<PassIcon sx={{ color: '#2e7d32 !important', fontSize: '16px' }} />} label="ผ่านเกณฑ์" sx={{ bgcolor: 'rgba(46, 125, 50, 0.09)', color: '#2e7d32', fontWeight: 'bold' }} size="small" />
                              ) : (
                                <Chip icon={<FailIcon sx={{ color: '#d32f2f !important', fontSize: '16px' }} />} label="ไม่ผ่านเกณฑ์" sx={{ bgcolor: 'rgba(211, 47, 47, 0.09)', color: '#d32f2f', fontWeight: 'bold' }} size="small" />
                              );
                            }
                            return <Chip label="รอประเมินภาระงาน" size="small" variant="outlined" color="warning" sx={{ fontWeight: 'bold' }} />;
                          }

                          const hasScores = [r.prof_score, r.spec_score, r.co_score].filter(s => s !== null && s !== undefined);
                          if (hasScores.length === 0) {
                            return <Chip label="ยังไม่ได้รับการนิเทศ" size="small" variant="outlined" sx={{ fontWeight: 'bold', color: 'text.secondary' }} />;
                          }
                          if (hasScores.length < 3) {
                            return <Chip label="อยู่ระหว่างการนิเทศ" size="small" variant="outlined" color="warning" sx={{ fontWeight: 'bold' }} />;
                          }
                          const avg = r.average_score ? parseFloat(r.average_score) : (hasScores.reduce((a, b) => a + Number(b), 0) / 3);
                          return avg >= 60 ? (
                            <Chip icon={<PassIcon sx={{ color: '#2e7d32 !important', fontSize: '16px' }} />} label="ผ่านเกณฑ์" sx={{ bgcolor: 'rgba(46, 125, 50, 0.09)', color: '#2e7d32', fontWeight: 'bold' }} size="small" />
                          ) : (
                            <Chip icon={<FailIcon sx={{ color: '#d32f2f !important', fontSize: '16px' }} />} label="ไม่ผ่านเกณฑ์" sx={{ bgcolor: 'rgba(211, 47, 47, 0.09)', color: '#d32f2f', fontWeight: 'bold' }} size="small" />
                          );
                        })()}
                        {r.remark && (
                          <Box sx={{ mt: 0.5 }}>
                            <MuiTooltip title={r.remark}>
                              <Chip 
                                label={`หมายเหตุ: ${r.remark}`} 
                                size="small" 
                                variant="outlined"
                                sx={{ 
                                  fontSize: '0.68rem', 
                                  height: 'auto', 
                                  py: 0.25, 
                                  borderColor: r.remark.includes('ไม่ผ่าน') ? '#ef5350' : '#ff9800',
                                  color: r.remark.includes('ไม่ผ่าน') ? '#c62828' : '#e65100',
                                  bgcolor: r.remark.includes('ไม่ผ่าน') ? '#ffebee' : '#fff8e1',
                                  maxWidth: 200,
                                  whiteSpace: 'normal',
                                  '& .MuiChip-label': { whiteSpace: 'normal', px: 1 }
                                }} 
                              />
                            </MuiTooltip>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="รายการต่อหน้า:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
            />
          </TableContainer>
        </Paper>
      </Box>

      {/* Student Detail Modal (Consolidated detailed-report print layout) */}
      <Dialog 
        open={openStudentDetail} 
        onClose={() => setOpenStudentDetail(false)} 
        fullWidth 
        maxWidth="lg"
        slotProps={{
          paper: {
            sx: { borderRadius: 4, overflow: 'hidden' }
          }
        }}
      >
        <DialogTitle className="no-print" sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: '#f8fafc',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
          px: 3,
          py: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
            รายละเอียดผลการประเมินรายบุคคล
          </Typography>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<PrintIcon />} 
            onClick={handlePrint} 
            className="no-print"
            sx={{ borderRadius: 2, fontWeight: 'bold', px: 2 }}
          >
            พิมพ์รายงาน
          </Button>
        </DialogTitle>
        
        <DialogContent dividers sx={{ px: 4, py: 3 }}>
          {loadingStudentDetail ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ mt: 2 }} color="textSecondary">กำลังดึงคะแนนและข้อเสนอแนะเชิงลึก...</Typography>
            </Box>
          ) : selectedReport ? (
            <Box id="printable-report">
              <Box className="screen-report-view">
                {(() => {
                  const requiresSupervision = selectedReport.student?.requires_supervision !== false && Number(selectedReport.student?.requires_supervision) !== 0;

                return (
                  <>
                    <Typography variant="h5" align="center" sx={{ fontWeight: 800, mb: 3, color: 'primary.main', fontSize: { xs: '1.25rem', md: '1.65rem' } }}>
                      {requiresSupervision ? 'รายงานสรุปผลการประเมินสมรรถนะและภาระงานนักศึกษา' : 'รายงานสรุปผลการประเมินภาระงานนักศึกษา'}
                    </Typography>

                    {!requiresSupervision && (
                      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                        <b>หมายเหตุกลุ่มรายวิชา:</b> รายวิชานี้ (<b>{selectedReport.student?.course_code || ''} {selectedReport.student?.course_name || ''}</b>) เป็นรายวิชาที่<b>ไม่ต้องรับการนิเทศ</b> ระบบจะประเมินเฉพาะคะแนนภาระงาน (Workload) และการสอบประมวลความรู้ (หากมี) โดยไม่ต้องรับการประเมินสมรรถนะจากคณะกรรมการนิเทศ
                      </Alert>
                    )}
                    
                    {/* Student Info */}
                    <Paper id="printable-report-info-card" sx={{ p: 2, px: 3, borderRadius: 3, bgcolor: '#fafafa', mb: 3, border: '1px solid rgba(0,0,0,0.03)' }}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <PersonIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="body2">
                              <b>ชื่อ-นามสกุลนักศึกษา:</b> {selectedReport.student.prefix || ''}{selectedReport.student.first_name} {selectedReport.student.last_name}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', pl: 4, mb: 1 }}>
                            <Typography variant="body2">
                              <b>รหัสนักศึกษา:</b> {selectedReport.student.student_code}
                            </Typography>
                          </Box>
                          {selectedReport.student.course_name && (
                            <Box sx={{ display: 'flex', alignItems: 'center', pl: 4 }}>
                              <CourseIcon sx={{ color: 'text.secondary', fontSize: 18, mr: 0.5 }} />
                              <Typography variant="body2">
                                <b>รายวิชา:</b> {selectedReport.student.course_code ? `${selectedReport.student.course_code} - ` : ''}{selectedReport.student.course_name}
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <SchoolIcon sx={{ color: 'success.main', mr: 1 }} />
                            <Typography variant="body2">
                              <b>โปรแกรมวิชา (สาขาวิชา):</b> {selectedReport.student.major}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', pl: 4, mb: 1 }}>
                            <Typography variant="body2">
                              <b>โรงเรียนฝึกประสบการณ์:</b> {selectedReport.student.school_name}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', pl: 4 }}>
                            <Typography variant="body2">
                              <b>ประเภทรายวิชา:</b> {requiresSupervision ? 'รายวิชาที่ต้องรับการนิเทศ' : 'รายวิชาที่ไม่ต้องรับการนิเทศ'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* SHOW COMPETENCY SECTION ONLY IF REQUIRES SUPERVISION */}
                    {requiresSupervision && (
                      <Box className="print-page-1">
                        {/* Committee */}
                        <Typography variant="subtitle2" className="committee-section-title" sx={{ fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                          <GroupIcon sx={{ mr: 1, color: 'text.secondary' }} /> คณะกรรมการประเมินสมรรถนะ
                        </Typography>
                        <Grid container spacing={2} className="committee-cards-container" sx={{ mb: 4 }}>
                          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                            <Paper className="committee-card" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, bgcolor: '#fbfdfe', border: '1px solid rgba(25, 118, 210, 0.06)' }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 600 }}>อาจารย์นิเทศก์ (วิชาชีพ):</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', mt: 0.5 }}>
                                {selectedReport.evaluators.prof || 'ยังไม่มีการประเมิน'}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                            <Paper className="committee-card" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, bgcolor: '#fffdfc', border: '1px solid rgba(156, 39, 176, 0.06)' }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 600 }}>อาจารย์นิเทศก์ (วิชาเอก):</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#9c27b0', mt: 0.5 }}>
                                {selectedReport.evaluators.spec1 || 'ยังไม่มีการประเมิน'}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                            <Paper className="committee-card" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, bgcolor: '#fcfdfb', border: '1px solid rgba(46, 125, 50, 0.06)' }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 600 }}>ครูพี่เลี้ยง:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main', mt: 0.5 }}>
                                {selectedReport.evaluators.co || 'ยังไม่มีการประเมิน'}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>

                {/* Score Rubrics Matrix */}
                <Typography variant="subtitle2" className="rubric-section-title" sx={{ fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                  <AssessmentIcon sx={{ mr: 1, color: 'text.secondary' }} /> คะแนนดิบแต่ละสมรรถนะ
                </Typography>
                <TableContainer component={Paper} variant="outlined" className="rubric-table-container" sx={{ borderRadius: 3, mb: 4, overflow: 'hidden' }}>
                  <Table size="small" sx={{ '& td, & th': { borderRight: '1px solid rgba(224, 224, 224, 1)' } }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell align="center" rowSpan={2} sx={{ fontWeight: 'bold', width: '15%', bgcolor: '#f1f5f9' }}>รายการประเมิน</TableCell>
                        <TableCell align="center" colSpan={3} sx={{ fontWeight: 'bold', bgcolor: 'rgba(25, 118, 210, 0.03)' }}>สมรรถนะด้านการปฏิบัติงาน (60 คะแนน)</TableCell>
                        <TableCell align="center" colSpan={5} sx={{ fontWeight: 'bold', bgcolor: 'rgba(46, 125, 50, 0.03)' }}>สมรรถนะด้านการปฏิบัติตน (40 คะแนน)</TableCell>
                      </TableRow>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell align="center" sx={{ minWidth: 70 }}><b>1.1</b><br/>ปฏิบัติหน้าที่ครู<br/>(20)</TableCell>
                        <TableCell align="center" sx={{ minWidth: 70 }}><b>1.2</b><br/>การจัดการเรียนรู้<br/>(24)</TableCell>
                        <TableCell align="center" sx={{ minWidth: 70 }}><b>1.3</b><br/>ความสัมพันธ์ชุมชน<br/>(16)</TableCell>
                        <TableCell align="center" sx={{ minWidth: 70 }}><b>2.1</b><br/>จรรยาบรรณวิชาชีพ<br/>(8)</TableCell>
                        <TableCell align="center" sx={{ minWidth: 70 }}><b>2.2</b><br/>จรรยาบรรณตนเอง<br/>(8)</TableCell>
                        <TableCell align="center" sx={{ minWidth: 70 }}><b>2.3</b><br/>จรรยาบรรณผู้รับบริการ<br/>(8)</TableCell>
                        <TableCell align="center" sx={{ minWidth: 70 }}><b>2.4</b><br/>จรรยาบรรณเพื่อนร่วมงาน<br/>(8)</TableCell>
                        <TableCell align="center" sx={{ minWidth: 70 }}><b>2.5</b><br/>จรรยาบรรณสังคม<br/>(8)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>คะแนนเฉลี่ย</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['1.1'] !== undefined ? selectedReport.sections['1.1'].toFixed(2) : '-'}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['1.2'] !== undefined ? selectedReport.sections['1.2'].toFixed(2) : '-'}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['1.3'] !== undefined ? selectedReport.sections['1.3'].toFixed(2) : '-'}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['2.1'] !== undefined ? selectedReport.sections['2.1'].toFixed(2) : '-'}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['2.2'] !== undefined ? selectedReport.sections['2.2'].toFixed(2) : '-'}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['2.3'] !== undefined ? selectedReport.sections['2.3'].toFixed(2) : '-'}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['2.4'] !== undefined ? selectedReport.sections['2.4'].toFixed(2) : '-'}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['2.5'] !== undefined ? selectedReport.sections['2.5'].toFixed(2) : '-'}</TableCell>
                      </TableRow>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>คะแนนแยกส่วน</TableCell>
                        <TableCell align="center" colSpan={3} sx={{ fontWeight: 700, color: 'primary.dark', fontSize: '1rem' }}>
                          {selectedReport.totals['1'] !== undefined ? `${selectedReport.totals['1'].toFixed(2)} / 60` : '-'}
                        </TableCell>
                        <TableCell align="center" colSpan={5} sx={{ fontWeight: 700, color: 'success.dark', fontSize: '1rem' }}>
                          {selectedReport.totals['2'] !== undefined ? `${selectedReport.totals['2'].toFixed(2)} / 40` : '-'}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ bgcolor: 'rgba(25, 118, 210, 0.06)' }}>
                        <TableCell colSpan={4} sx={{ fontWeight: 800, textAlign: 'right', py: 1.5 }}>คะแนนรวมสุทธิ (100 คะแนนเต็ม)</TableCell>
                        <TableCell colSpan={5} align="center" sx={{ fontWeight: 900, fontSize: '1.4rem', color: 'primary.main', py: 1.5 }}>
                          {selectedReport.totals['grand'] !== undefined ? `${selectedReport.totals['grand'].toFixed(2)}%` : '-'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                        {/* Status Summary Banner */}
                        <Box className="status-summary-banner print-only" sx={{ 
                          display: 'none',
                          mt: 2, 
                          p: 2.5, 
                          borderRadius: 3, 
                          textAlign: 'center',
                          background: selectedReport.roundCount === 3 
                            ? (selectedReport.totals['grand'] >= 60 ? 'linear-gradient(to right, #e8f5e9, #c8e6c9)' : 'linear-gradient(to right, #ffebee, #ffcdd2)') 
                            : 'linear-gradient(to right, #fff3e0, #ffe0b2)',
                          color: selectedReport.roundCount === 3 
                            ? (selectedReport.totals['grand'] >= 60 ? '#1b5e20' : '#b71c1c') 
                            : '#e65100',
                          border: '1px solid rgba(0,0,0,0.03)'
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            ผลการประเมินสมรรถนะสรุป: {
                              selectedReport.roundCount === 3 
                                ? (selectedReport.totals['grand'] >= 60 ? 'ผ่านเกณฑ์สมรรถนะ (PASSED)' : 'ไม่ผ่านเกณฑ์สมรรถนะ (FAILED)') 
                                : `รอดำเนินการประเมิน (PENDING) - ได้รับผลประเมินแล้ว ${selectedReport.roundCount} / 3 ส่วน`
                            }
                          </Typography>
                          {selectedReport.roundCount === 3 && (
                            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                              คะแนนเฉลี่ยรวม 3 ฝ่ายสุทธิคิดเป็น {selectedReport.totals['grand']?.toFixed(2)}% (เกณฑ์ผ่านขั้นต่ำของคณะคือ 60%)
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* SECTION: WORKLOAD EVALUATION (คะแนนประเมินภาระงาน) */}
                    <Box sx={{ mt: 4, mb: 4 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                        <WorkloadIcon sx={{ mr: 1, color: 'info.main' }} /> คะแนนประเมินภาระงาน (Workload Evaluation)
                      </Typography>

                      {selectedReport.workload ? (
                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                          <Grid container spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
                            <Grid size={{ xs: 12, sm: 8 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                แบบประเมิน: {selectedReport.workload.form_title || 'แบบประเมินภาระงาน'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                ผู้ประเมิน: <b>{selectedReport.workload.evaluator_name || 'อาจารย์นิเทศก์'}</b> | ประเมินเมื่อ: {selectedReport.workload.evaluated_at ? new Date(selectedReport.workload.evaluated_at).toLocaleDateString('th-TH') : '-'}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 600 }}>
                                รวมคะแนนภาระงาน
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                                  {selectedReport.workload.total_score !== null && selectedReport.workload.total_score !== undefined ? selectedReport.workload.total_score : (selectedReport.workload.score !== null && selectedReport.workload.score !== undefined ? selectedReport.workload.score : '-')}
                                </Typography>
                                <Typography variant="body1" color="textSecondary" sx={{ fontWeight: 600 }}>
                                  / {selectedReport.workload.max_score}
                                </Typography>
                                <Chip 
                                  label={selectedReport.workload.is_passed ? `ผ่าน (${selectedReport.workload.percentage ? selectedReport.workload.percentage.toFixed(1) : '0.0'}%)` : `ไม่ผ่าน (${selectedReport.workload.percentage ? selectedReport.workload.percentage.toFixed(1) : '0.0'}%)`}
                                  color={selectedReport.workload.is_passed ? 'success' : 'error'}
                                  size="small"
                                  sx={{ fontWeight: 700, ml: 1 }}
                                />
                              </Box>
                            </Grid>
                          </Grid>

                          {/* Rubric Breakdown table */}
                          {selectedReport.workload.items && selectedReport.workload.items.length > 0 && (
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, bgcolor: '#ffffff' }}>
                              <Table size="small">
                                <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', width: '60%' }}>รายการภาระงาน / งานที่มอบหมาย</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', width: '20%' }}>คะแนนเต็ม</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', width: '20%' }}>คะแนนที่ได้</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {selectedReport.workload.items.map((item: any, idx: number) => (
                                    <TableRow key={item.id || idx} hover>
                                      <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {item.item_code ? `${item.item_code} ` : ''}{item.item_name || item.title || `ภาระงานข้อที่ ${idx + 1}`}
                                        </Typography>
                                        {item.description && (
                                          <Typography variant="caption" color="textSecondary">
                                            {item.description}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {item.max_score}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: item.score !== null ? 'primary.main' : 'text.disabled' }}>
                                          {item.score !== null ? item.score : '-'}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </Paper>
                      ) : (
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                          ยังไม่มีการบันทึกคะแนนประเมินภาระงานโดยอาจารย์นิเทศก์สำหรับนักศึกษารายนี้
                        </Alert>
                      )}
                    </Box>

                    {/* SECTION: COMPREHENSIVE EXAM SCORE */}
                    {(selectedReport.student?.requires_exam || selectedReport.exam) && (
                      <Box sx={{ mt: 3, mb: 4 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                          <QuizIcon sx={{ mr: 1, color: 'secondary.main' }} /> ผลคะแนนการสอบประมวลความรู้ (Comprehensive Exam)
                        </Typography>

                        {selectedReport.exam ? (
                          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#fdf4ff', border: '1px solid #f0abfc' }}>
                            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                              <Grid size={{ xs: 12, sm: 7 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#581c87' }}>
                                  {selectedReport.exam.exam_title || 'การสอบประมวลผลความรู้'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                  สถานะ: <b>{selectedReport.exam.status}</b> | ส่งข้อสอบเมื่อ: {selectedReport.exam.submitted_at ? new Date(selectedReport.exam.submitted_at).toLocaleString('th-TH') : '-'}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 5 }} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 600 }}>
                                  คะแนนสอบที่ได้
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
                                  <Typography variant="h4" sx={{ fontWeight: 800, color: selectedReport.exam.is_passed ? 'success.main' : 'error.main' }}>
                                    {selectedReport.exam.score} / {selectedReport.exam.total_points}
                                  </Typography>
                                  <Chip 
                                    label={selectedReport.exam.is_passed ? `ผ่านเกณฑ์ (${selectedReport.exam.percentage.toFixed(1)}%)` : `ไม่ผ่านเกณฑ์ (${selectedReport.exam.percentage.toFixed(1)}%)`}
                                    color={selectedReport.exam.is_passed ? 'success' : 'error'}
                                    size="small"
                                    sx={{ fontWeight: 700 }}
                                  />
                                </Box>
                              </Grid>
                            </Grid>
                          </Paper>
                        ) : (
                          <Alert severity="info" sx={{ borderRadius: 2 }}>
                            รายวิชานี้กำหนดให้มีการสอบประมวลความรู้ แต่นักศึกษายังไม่ได้เข้าสอบหรืออยู่ระหว่างรอส่งผลการสอบ
                          </Alert>
                        )}
                      </Box>
                    )}

                    {/* Overall Summary for non-supervision course */}
                    {!requiresSupervision && selectedReport.workload && (
                      <Box className="status-summary-banner" sx={{ 
                        mt: 2, 
                        p: 2.5, 
                        borderRadius: 3, 
                        textAlign: 'center', 
                        background: selectedReport.workload.is_passed 
                          ? 'linear-gradient(to right, #e8f5e9, #c8e6c9)' 
                          : 'linear-gradient(to right, #ffebee, #ffcdd2)', 
                        color: selectedReport.workload.is_passed ? '#1b5e20' : '#b71c1c', 
                        border: '1px solid rgba(0,0,0,0.03)' 
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          ผลการประเมินสรุป: {selectedReport.workload.is_passed ? 'ผ่านเกณฑ์ภาระงาน (PASSED)' : 'ไม่ผ่านเกณฑ์ภาระงาน (FAILED)'}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                          คะแนนรวมภาระงานคิดเป็น {selectedReport.workload.percentage.toFixed(1)}% (เกณฑ์ผ่านขั้นต่ำของคณะคือ 60%)
                        </Typography>
                      </Box>
                    )}

                    {/* PAGE 2: Feedbacks (SHOW ONLY IF REQUIRES SUPERVISION) */}
                    {requiresSupervision && (
                      <Box className="print-page-2" sx={{ mt: 4 }}>
                        {/* Print Only Header for Page 2 */}
                        <Box className="print-only">
                          <Typography variant="h5" align="center" sx={{ fontWeight: 800, mb: 1, color: 'primary.main' }}>
                            ความคิดเห็นและข้อเสนอแนะเชิงคุณภาพ
                          </Typography>
                          <Typography variant="subtitle2" align="center" sx={{ mb: 2, color: 'text.secondary' }}>
                            นักศึกษา: {selectedReport.student.prefix || ''}{selectedReport.student.first_name} {selectedReport.student.last_name} ({selectedReport.student.student_code}) | สาขาวิชา: {selectedReport.student.major}
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                        </Box>
                <Typography variant="subtitle2" className="feedback-section-title" sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'center' }}>
                  <FeedbackCommentIcon sx={{ mr: 1, color: 'primary.main' }} /> ความคิดเห็นและข้อเสนอแนะเชิงคุณภาพ
                </Typography>
                
                <Grid container spacing={3} className="feedback-cards-container">
                  {/* Professional Feedback */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Paper className="feedback-card" sx={{ 
                      p: 2.5, 
                      borderRadius: 3, 
                      bgcolor: 'rgba(25, 118, 210, 0.02)', 
                      border: '1px solid rgba(25, 118, 210, 0.1)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#1976d2', width: 32, height: 32, mr: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white' }}>ครู</Typography>
                        </Avatar>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1976d2' }}>อาจารย์นิเทศก์ (วิชาชีพ)</Typography>
                      </Box>
                      <Divider sx={{ mb: 1.5 }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontStyle: selectedReport.feedbacks?.prof ? 'normal' : 'italic', 
                          color: selectedReport.feedbacks?.prof ? 'text.primary' : 'text.secondary',
                          whiteSpace: 'pre-line',
                          lineHeight: 1.6,
                          flexGrow: 1
                        }}
                      >
                        {selectedReport.feedbacks?.prof || '* ไม่ได้กรอกความคิดเห็นเพิ่มเติม...'}
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Specialist Feedback */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Paper className="feedback-card" sx={{ 
                      p: 2.5, 
                      borderRadius: 3, 
                      bgcolor: 'rgba(156, 39, 176, 0.02)', 
                      border: '1px solid rgba(156, 39, 176, 0.1)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#9c27b0', width: 32, height: 32, mr: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white' }}>เอก</Typography>
                        </Avatar>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#9c27b0' }}>อาจารย์นิเทศก์ (วิชาเอก)</Typography>
                      </Box>
                      <Divider sx={{ mb: 1.5 }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontStyle: selectedReport.feedbacks?.spec1 ? 'normal' : 'italic', 
                          color: selectedReport.feedbacks?.spec1 ? 'text.primary' : 'text.secondary',
                          whiteSpace: 'pre-line',
                          lineHeight: 1.6,
                          flexGrow: 1
                        }}
                      >
                        {selectedReport.feedbacks?.spec1 || '* ไม่ได้กรอกความคิดเห็นเพิ่มเติม...'}
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Co-teacher Feedback */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Paper className="feedback-card" sx={{ 
                      p: 2.5, 
                      borderRadius: 3, 
                      bgcolor: 'rgba(46, 125, 50, 0.02)', 
                      border: '1px solid rgba(46, 125, 50, 0.1)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#2e7d32', width: 32, height: 32, mr: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white' }}>เลี้ยง</Typography>
                        </Avatar>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2e7d32' }}>ครูพี่เลี้ยง (Mentor)</Typography>
                      </Box>
                      <Divider sx={{ mb: 1.5 }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontStyle: selectedReport.feedbacks?.co ? 'normal' : 'italic', 
                          color: selectedReport.feedbacks?.co ? 'text.primary' : 'text.secondary',
                          whiteSpace: 'pre-line',
                          lineHeight: 1.6,
                          flexGrow: 1
                        }}
                      >
                        {selectedReport.feedbacks?.co || '* ไม่ได้กรอกความคิดเห็นเพิ่มเติม...'}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Status Summary Banner */}
                <Box className="status-summary-banner" sx={{ 
                  mt: 4, 
                  p: 2.5, 
                  borderRadius: 3, 
                  textAlign: 'center',
                  background: selectedReport.roundCount === 3 
                    ? (selectedReport.totals['grand'] >= 60 ? 'linear-gradient(to right, #e8f5e9, #c8e6c9)' : 'linear-gradient(to right, #ffebee, #ffcdd2)') 
                    : 'linear-gradient(to right, #fff3e0, #ffe0b2)',
                  color: selectedReport.roundCount === 3 
                    ? (selectedReport.totals['grand'] >= 60 ? '#1b5e20' : '#b71c1c') 
                    : '#e65100',
                  border: '1px solid rgba(0,0,0,0.03)'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    ผลการประเมินสรุป: {
                      selectedReport.roundCount === 3 
                        ? (selectedReport.totals['grand'] >= 60 ? 'ผ่านเกณฑ์สมรรถนะ (PASSED)' : 'ไม่ผ่านเกณฑ์สมรรถนะ (FAILED)') 
                        : `รอดำเนินการประเมิน (PENDING) - ได้รับผลประเมินแล้ว ${selectedReport.roundCount} / 3 ส่วน`
                    }
                  </Typography>
                  {selectedReport.roundCount === 3 && (
                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                      คะแนนเฉลี่ยรวม 3 ฝ่ายสุทธิคิดเป็น {selectedReport.totals['grand']?.toFixed(2)}% (เกณฑ์ผ่านขั้นต่ำของคณะคือ 60%)
                    </Typography>
                  )}
                        </Box>
                      </Box>
                    )}
                  </>
                );
              })()}
              </Box>

              {/* Official Print Layout (Rendered only during print) */}
              <OfficialEvaluationReportPrint report={selectedReport} />

              <style>
                {`
                  .official-print-document {
                    display: none !important;
                  }
                  @media print {
                    @page {
                      size: A4 portrait;
                      margin: 6mm 8mm;
                    }
                    body {
                      background: #ffffff !important;
                      -webkit-print-color-adjust: exact !important;
                      print-color-adjust: exact !important;
                    }
                    #root,
                    .screen-report-view,
                    .no-print,
                    .MuiDialogTitle-root,
                    .MuiDialogActions-root {
                      display: none !important;
                    }
                    .official-print-document {
                      display: block !important;
                      width: 100% !important;
                    }
                    .MuiDialog-root {
                      position: absolute !important;
                      left: 0 !important;
                      top: 0 !important;
                      width: 100% !important;
                      height: auto !important;
                      overflow: visible !important;
                    }
                    .MuiBackdrop-root {
                      display: none !important;
                    }
                    .MuiDialog-container {
                      height: auto !important;
                      display: block !important;
                      padding: 0 !important;
                    }
                    .MuiDialog-paper {
                      box-shadow: none !important;
                      border: none !important;
                      margin: 0 !important;
                      padding: 0 !important;
                      width: 100% !important;
                      max-width: 100% !important;
                      position: absolute !important;
                      left: 0 !important;
                      top: 0 !important;
                      height: auto !important;
                      overflow: visible !important;
                      background: transparent !important;
                    }
                    .MuiDialogContent-root {
                      border: none !important;
                      padding: 0 !important;
                      overflow: visible !important;
                    }
                    #printable-report {
                      padding: 0 !important;
                      margin: 0 !important;
                      width: 100% !important;
                    }
                  }
                `}
              </style>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderTop: '1px solid rgba(0,0,0,0.05)' }} className="no-print">
          <Button 
            variant="contained" 
            onClick={() => setOpenStudentDetail(false)}
            sx={{ px: 3, borderRadius: 2, fontWeight: 'bold' }}
          >
            ปิดหน้าต่าง
          </Button>
        </DialogActions>
      </Dialog>

      {/* School Comments Dialog */}
      <Dialog open={openSchoolDetail} onClose={() => setOpenSchoolDetail(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h6">ความคิดเห็นของนักศึกษาที่มีต่อ: {selectedSchoolData?.name}</Typography>
          <IconButton onClick={() => setOpenSchoolDetail(false)} size="small" sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
          {schoolDetailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : (
            <Box>
              {schoolEvaluations.length > 0 && (
                <Typography variant="subtitle2" sx={{ mb: 3, color: 'text.secondary', textAlign: 'center' }}>
                  พบทั้งหมด {schoolEvaluations.length} ความคิดเห็นจากนักศึกษา
                </Typography>
              )}
              <Grid container spacing={2}>
                {schoolEvaluations.map((evalItem: any) => (
                  <Grid size={{ xs: 12 }} key={evalItem.id}>
                    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.03)', borderLeft: '5px solid', borderColor: 'secondary.main' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            โดย: {evalItem.first_name} {evalItem.last_name}
                          </Typography>
                          <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                            สาขาวิชา: {evalItem.major}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ปีการศึกษา {evalItem.semester}/{evalItem.academic_year}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Rating value={evalItem.rating} readOnly size="small" />
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }} color="textSecondary">
                            {new Date(evalItem.created_at).toLocaleDateString('th-TH', { 
                              year: 'numeric', month: 'short', day: 'numeric' 
                            })}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Divider sx={{ mb: 2, borderStyle: 'dashed' }} />
                      
                      <Box sx={{ position: 'relative', pl: 2 }}>
                        <Typography variant="body2" sx={{ 
                          fontStyle: 'italic', 
                          color: 'text.primary',
                          lineHeight: 1.6,
                          '&:before': {
                            content: '"“"',
                            position: 'absolute',
                            left: -10,
                            top: -10,
                            fontSize: '2rem',
                            color: 'grey.300',
                            fontFamily: 'serif'
                          }
                        }}>
                          {evalItem.feedback || 'ไม่มีข้อความคอมเมนต์'}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              {schoolEvaluations.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <AssignmentIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                  <Typography color="textSecondary">ยังไม่มีรายละเอียดคำติชมสำหรับโรงเรียนนี้</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenSchoolDetail(false)} variant="contained">ปิดหน้าต่าง</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
